import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import AccessibleTable from './AccessibleTable';
import axios from 'axios';
import UndergraduateApplicationForm from './UndergraduateApplicationForm';
import UndergraduateApplicationGuidance from './UndergraduateApplicationGuidance';
import { useImpersonation } from './StudentWorkspace';
import { ApplicationSummary, GraduationApi, Overview, errorMessage } from '../services/GraduationApi';

export function OverviewDetails({ overview }: { overview: Overview }) {
  return <>
    <section className="section-panel mb-3" aria-labelledby="selected-application-heading">
      <h2 id="selected-application-heading">Application #{overview.applicationId}</h2>
      <p><span className="admin-status-label">{overview.status}</span>{overview.archived && <span className="ms-2 text-secondary">Archived</span>}</p>
      {overview.statusDescription && <p>{overview.statusDescription}</p>}
      <dl className="data-summary"><dt>Diploma name</dt><dd>{overview.diplomaName || 'Not provided'}</dd>
        <dt>Expected graduation term</dt><dd>{overview.graduationTerm || overview.graduationTermId || 'Not provided'}</dd></dl>
    </section>
    <section className="section-panel mb-3" aria-labelledby="selected-areas-heading">
      <h2 id="selected-areas-heading">Selected areas of study</h2>
      {overview.areas.length === 0 ? <div className="empty-state">No selected areas are available in the current academic record.</div> :
      <ul className="data-list">{overview.areas.map((area, index) => <li key={`${area.areaStudyId}-${index}`}>
        {area.description || area.areaStudyId || 'Area of study'}{area.concentration && ` — ${area.concentration}`}
        {area.degreeCode && ` (${area.degreeCode})`}
      </li>)}</ul>}
    </section>
    <section className="section-panel mb-3" aria-labelledby="application-notes-heading">
      <h2 id="application-notes-heading">Application notes</h2>
      {overview.notes.length === 0 ? <p>No notes have been added to this application.</p> :
        <ul className="data-list">{overview.notes.map(note => <li key={note.id}>
          <p className="mb-1">{note.text}</p>
          <small className="text-secondary">{note.author} · <time dateTime={note.submittedOn}>{new Date(note.submittedOn).toLocaleDateString()}</time></small>
        </li>)}</ul>}
    </section>
  </>;
}
export default function UndergraduateOverview() {
  const impersonation = useImpersonation();
  const canEdit = !impersonation || impersonation.session.mode === 'Edit';
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [saved, setSaved] = useState<number | null>(null);
  const [success, setSuccess] = useState('');
  const [applications, setApplications] = useState<ApplicationSummary[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setApplications(null); setSelected(null); setOverview(null); setError('');
    (impersonation ? GraduationApi.studentApplications(controller.signal) : GraduationApi.applications(controller.signal)).then(items => {
      const linked = Number(new URLSearchParams(window.location.search).get('applicationId'));
      setApplications(items); setSelected(saved ?? (items.some(item => item.applicationId === linked) ? linked : items[0]?.applicationId ?? null));
    })
      .catch(e => { if (!axios.isCancel(e)) setError(errorMessage(e)); });
    return () => controller.abort();
  }, [retry, saved, impersonation]);
  useEffect(() => {
    setOverview(null);
    setError('');
    if (selected === null || (impersonation && applications?.find(a => a.applicationId === selected)?.applicationType !== 3)) return;
    const controller = new AbortController();
    setError('');
    GraduationApi.overview(selected, controller.signal).then(setOverview)
      .catch(e => { if (!axios.isCancel(e)) setError(errorMessage(e)); });
    return () => controller.abort();
  }, [selected, retry, saved, applications, impersonation]);
  if (editing !== null && canEdit) return <UndergraduateApplicationForm applicationId={editing === 'new' ? null : editing}
    onCancel={() => setEditing(null)} onSaved={id => { setEditing(null); setSaved(id); setRetry(v => v + 1); setSuccess(editing === 'new' ? `Application #${id} was submitted successfully.` : `Application #${id} changes were saved.`); }} />;
  return <section aria-labelledby="overview-heading">
    <h1 id="overview-heading">{impersonation ? 'Student applications' : 'Undergraduate application overview'}</h1>
    <UndergraduateApplicationGuidance defaultExpanded />
    {!canEdit ? <p>This overview is read-only. Contact Registration and Academic Records if you need help with your application.</p> :
      <Button className="mb-3" onClick={() => { setSuccess(''); setEditing('new'); }}>Create undergraduate application</Button>}
    {success && <Alert variant="success" role="status">{success}</Alert>}
    {error && <Alert variant="danger" role="alert">{error} <Button variant="outline-danger" onClick={() => setRetry(value => value + 1)}>Retry</Button></Alert>}
    {!error && applications === null && <p role="status">Loading applications…</p>}
    {applications?.length === 0 && <Alert variant="info">No {impersonation ? '' : 'undergraduate '}applications were found for this student.</Alert>}
    {applications && applications.length > 0 && <section className="section-panel mb-3" aria-labelledby="student-applications-heading">
      <h2 id="student-applications-heading">Applications</h2>
      <AccessibleTable scrollLabel="Student application list" striped hover aria-label="Student application list">
      <thead><tr><th scope="col">Application</th><th scope="col">Type</th><th scope="col">Graduation term</th><th scope="col">Status</th><th scope="col">Action</th></tr></thead>
      <tbody>{applications.map(app => <tr key={app.applicationId} aria-selected={selected === app.applicationId}>
        <th scope="row">#{app.applicationId}</th><td>{app.applicationType === 3 || (!impersonation && app.applicationType === undefined) ? 'Undergraduate' : app.applicationType === 0 ? 'Doctoral' : app.applicationType === 1 ? 'Masters or Specialist' : app.applicationType === 2 ? 'Certificate' : 'Not specified'}</td>
        <td>{app.graduationTerm || 'Not provided'}</td><td><span className="admin-status-label">{app.status}{app.archived && ' (Archived)'}</span></td>
        <td><div className="application-row-actions">
          <Button size="sm" variant="outline-primary" aria-label={`View application #${app.applicationId}`} onClick={() => { if (selected !== app.applicationId) { setOverview(null); setSelected(app.applicationId); } }}>View</Button>
          {canEdit && app.applicationType === 3 && <Button size="sm" variant="primary" aria-label={`Edit application #${app.applicationId}`}
            disabled={!app.canEdit} onClick={() => { setSuccess(''); setEditing(app.applicationId); }}>Edit</Button>}
        </div></td>
      </tr>)}</tbody>
      </AccessibleTable>
    </section>}
    {impersonation && selected !== null && applications?.find(a => a.applicationId === selected)?.applicationType !== 3 &&
      <Alert variant="info">This application is listed for reference. Graduate application details and editing are not available in this workspace.</Alert>}
    {!error && selected !== null && (!impersonation || applications?.find(a => a.applicationId === selected)?.applicationType === 3) && !overview && <p role="status">Loading application details…</p>}
    {!error && overview && <><OverviewDetails overview={overview} />
      {canEdit && overview.canEdit && <Button onClick={() => { setSuccess(''); setEditing(overview.applicationId); }}>Edit application</Button>}</>}
  </section>;
}






